import { IconNames } from '@blueprintjs/icons';
import { DateFormats, formatDate } from '@strata/helper-functions';
import { Grid, SecondaryData } from '@strata/react-grid';
import { MessagingBlock } from '@strata/react-messaging-block';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { ContingentTherapyRow } from 'components/shared/ContingentTherapyRow';
import { useGetPrescriberDetailsQuery, useGetSupplierDetailsQuery } from 'store/api/referenceApi';

const CONTINGENT_THERAPY_ROW = 'CONTINGENT_THERAPY_ROW';

const initialSortProperties = [{ id: 'authStart', desc: true }];

/* eslint-disable react/prop-types */
const NPICell = ({ getValue }) => {
    const { data: supplierDetails } = useGetSupplierDetailsQuery(
        { supplierNpi: getValue() },
        { skip: !getValue() },
    );
    const { data: prescriberDetails } = useGetPrescriberDetailsQuery(
        { prescriberNpi: getValue() },
        { skip: !getValue() || supplierDetails?.organizationName },
    );

    const getDisplayText = (npi) => {
        if (supplierDetails?.organizationName) {
            return `${supplierDetails.organizationName} (${npi})`;
        }
        if (prescriberDetails?.prescriberName) {
            return `${prescriberDetails.prescriberName} (${npi})`;
        }
        return npi;
    };

    return <SecondaryData data={getDisplayText(getValue()?.trim())} />;
};
/* eslint-enable react/prop-types */

const columns = [
    {
        accessorKey: 'authSource',
        header: 'Auth Source',
        size: 150,
        enableSorting: false,
        cell: ({ getValue, row }) =>
            row.original.caseId === CONTINGENT_THERAPY_ROW ? (
                <ContingentTherapyRow />
            ) : (
                <SecondaryData data={getValue()} />
            ),
    },
    {
        accessorKey: 'authStart',
        header: 'Auth Start',
        size: 110,
        cell: ({ getValue }) => <SecondaryData data={formatDate(getValue())} />,
    },
    {
        accessorKey: 'authExpiration',
        header: 'Auth End',
        size: 110,
        cell: ({ getValue }) => <SecondaryData data={formatDate(getValue())} />,
    },
    {
        accessorKey: 'caseId',
        header: 'Auth ID',
        size: 150,
        enableSorting: false,
    },
    {
        accessorKey: 'receivedAt',
        header: 'Received At',
        size: 170,
        cell: ({ getValue }) => (
            <SecondaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
        ),
    },
    {
        accessorKey: 'requestingProvider',
        header: 'Requesting Provider',
        size: 150,
        enableSorting: false,
        cell: NPICell,
    },
    {
        accessorKey: 'servicingFacility',
        header: 'Servicing Facility',
        size: 150,
        enableSorting: false,
        cell: NPICell,
    },
    {
        accessorKey: 'servicingProvider',
        header: 'Servicing Provider',
        size: 150,
        enableSorting: false,
        cell: NPICell,
    },
    {
        accessorKey: 'billingProvider',
        header: 'Billing Provider',
        size: 150,
        enableSorting: false,
        cell: NPICell,
    },
];

export const AuthDetails = ({
    auths = undefined,
    isContingentTherapy = false,
    isLoading = true,
}) => {
    const gridData = [
        // Displays the Contingent Therapy row pinned to the top of the grid
        ...(isContingentTherapy ? [{ caseId: CONTINGENT_THERAPY_ROW, pinTop: true }] : []),
        ...(auths ?? []),
    ];
    return (
        <Grid
            data={gridData}
            isLoading={isLoading}
            sortProperties={initialSortProperties}
            noResults={
                <div className="padding__around--large">
                    <MessagingBlock
                        size={MessagingBlock.Sizes.SMALL}
                        icon={IconNames.DOCUMENT}
                        primaryText="No Authorizations available"
                    />
                </div>
            }
            rowCssClass={(rowData) =>
                classNames({
                    'griddle-row--contingent-therapy': rowData.caseId === CONTINGENT_THERAPY_ROW,
                })
            }
            columns={columns}
        />
    );
};

AuthDetails.propTypes = {
    auths: PropTypes.arrayOf(
        PropTypes.shape({
            authSource: PropTypes.string,
            caseId: PropTypes.string,
            authStart: PropTypes.string,
            authExpiration: PropTypes.string,
            receivedAt: PropTypes.string,
            billingProvider: PropTypes.string,
            servicingProvider: PropTypes.string,
            requestingProvider: PropTypes.string,
            servicingFacility: PropTypes.string,
        }),
    ),
    isContingentTherapy: PropTypes.bool,
    isLoading: PropTypes.bool,
};
